import React, { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Spinner } from '@billda/design-system';
import { useInvitationFlowStore } from '../stores/invitationFlowStore';
import BusinessDetailsCaptureForm from '../components/BusinessDetailsCaptureForm';
import { AuthLayout } from '../../../shared/components/auth';

/**
 * Page for capturing business details during invitation flow
 * This page is shown after the user has accepted the invitation and received tokens
 */
const BusinessDetailsCapturePage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  // Get invitation data and actions from store
  const invitationInfo = useInvitationFlowStore(state => state.invitationInfo);
  const requiresBusinessDetails = useInvitationFlowStore(
    state => state.requiresBusinessDetails
  );
  const businessDetailsCaptured = useInvitationFlowStore(
    state => state.businessDetailsCaptured
  );
  const markBusinessDetailsCaptured = useInvitationFlowStore(
    state => state.markBusinessDetailsCaptured
  );

  // Check prerequisites and redirect if needed
  useEffect(() => {
    if (!id) {
      navigate('/invitation/error', { replace: true });
      return;
    }

    // If business details not required or already captured, go to home
    if (!requiresBusinessDetails || businessDetailsCaptured) {
      navigate('/', { replace: true });
    }
  }, [id, requiresBusinessDetails, businessDetailsCaptured, navigate]);

  // Handle successful business details capture (also used for skip)
  const handleSuccess = () => {
    markBusinessDetailsCaptured();
    navigate('/', { replace: true });
  };

  // Show loading if prerequisites not met
  if (!id || !requiresBusinessDetails) {
    return (
      <AuthLayout variant="invite">
        <div className="flex min-h-[200px] items-center justify-center">
          <Spinner />
        </div>
      </AuthLayout>
    );
  }

  // Render the business details capture form
  return (
    <AuthLayout
      variant="invite"
      organizationName={invitationInfo?.organisationName}
    >
      <BusinessDetailsCaptureForm
        organisationName={invitationInfo?.organisationName || ''}
        onSuccess={handleSuccess}
        onError={message => {
          console.error('Business details capture error:', message);
        }}
      />
    </AuthLayout>
  );
};

export default BusinessDetailsCapturePage;
